import type { ReactNode } from 'react';

/**
 * Alert service: keeps a list of alerts and notifies subscribers when the list changes.
 * Based on assets/js/services/alert.js (xpAlert).
 */

export const AlertType = {
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
  INFO: 'INFO',
} as const;

export type AlertTypeValue = (typeof AlertType)[keyof typeof AlertType];

export const AlertKind = {
  GENERAL: 'GENERAL',
  TRIAL: 'TRIAL',
  ACADEMY: 'ACADEMY',
} as const;

export type AlertKindValue = (typeof AlertKind)[keyof typeof AlertKind];

export interface AlertEntry {
  id: string;
  type: AlertTypeValue;
  kind: AlertKindValue;
  message: ReactNode;
  close: () => void;
}

type Subscriber = () => void;

let alerts: AlertEntry[] = [];
const subscribers = new Set<Subscriber>();
let idCounter = 0;

function nextId(): string {
  idCounter += 1;
  return `alert-${idCounter}`;
}

function notify(): void {
  subscribers.forEach((cb) => cb());
}

export function getAlerts(): AlertEntry[] {
  return alerts;
}

export function clearAlerts(kind?: AlertKindValue): void {
  if (kind) {
    alerts = alerts.filter((alert) => alert.kind !== kind);
  } else {
    alerts = [];
  }
  notify();
}

export function removeAlert(entry: AlertEntry): void {
  const prev = alerts;
  alerts = alerts.filter((a) => a !== entry);
  if (alerts.length !== prev.length) {
    notify();
  }
}

export function addAlert(
  type: AlertTypeValue,
  message: ReactNode,
  clear?: boolean,
  kind?: AlertKindValue
): () => void {
  if (clear === true) {
    clearAlerts();
  }

  const resolvedKind = kind ?? AlertKind.GENERAL;
  const id = nextId();
  const entry: AlertEntry = {
    id,
    type,
    kind: resolvedKind,
    message,
    close: () => removeAlert(entry),
  };

  alerts.push(entry);
  notify();

  return entry.close;
}

export function error(
  message: ReactNode,
  clear?: boolean,
  kind?: AlertKindValue
): () => void {
  return addAlert(AlertType.ERROR, message, clear, kind);
}

export function success(
  message: ReactNode,
  clear?: boolean,
  kind?: AlertKindValue
): () => void {
  return addAlert(AlertType.SUCCESS, message, clear, kind);
}

export function info(
  message: ReactNode,
  clear?: boolean,
  kind?: AlertKindValue
): () => void {
  return addAlert(AlertType.INFO, message, clear, kind);
}

export function subscribe(callback: Subscriber): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

const AlertService = {
  AlertType,
  AlertKind,
  getAlerts,
  clearAlerts,
  removeAlert,
  addAlert,
  error,
  success,
  info,
  subscribe,
};

export default AlertService;
